import { createContext, Dispatch, ReactNode, useCallback, useContext, useMemo, useReducer } from "react";
import { IAttributedUser } from "@DataPages/types/socialMediaReviews/model";

type FilterState = {
	ratings: number[];
	text: string;
	users: IAttributedUser[];
};

type Action =
	| { type: "SET_RATING"; payload: number[] }
	| { type: "SET_TEXT"; payload: string }
	| { type: "SET_USERS"; payload: IAttributedUser[] }
	| { type: "RESET" };

type Ctx = {
	filter: FilterState;
	dispatch: Dispatch<Action>;
	onSelectAssociate: (users: IAttributedUser[]) => void;
	onSearchInputChange: (value: string) => void;
	onRatingChange: (value: number[]) => void;
};

const initialState: FilterState = { ratings: [], text: "", users: [] };

function filterReducer(state: FilterState, action: Action): FilterState {
	switch (action.type) {
		case "SET_RATING":
			return { ...state, ratings: action.payload };
		case "SET_TEXT":
			return { ...state, text: action.payload };
		case "SET_USERS":
			return { ...state, users: action.payload };
		case "RESET":
			return { ...initialState };
		default:
			return state;
	}
}

const SocialMediaReviewDetailedDialogContext = createContext<Ctx | undefined>(undefined);

function SocialMediaReviewDetailedDialogProvider({ children }: { children: ReactNode }) {
	const [filter, dispatch] = useReducer(filterReducer, initialState);

	const handleRatingChange = useCallback((ratings: number[]) => {
		dispatch({ type: "SET_RATING", payload: ratings });
	}, []);

	const handleSearchInputChange = useCallback((criteria: string) => {
		dispatch({ type: "SET_TEXT", payload: criteria });
	}, []);

	const handleSelectAssociate = useCallback((selectedUsers: IAttributedUser[]) => {
		dispatch({ type: "SET_USERS", payload: selectedUsers });
	}, []);

	const value = useMemo<Ctx>(
		() => ({
			filter,
			dispatch,
			onRatingChange: handleRatingChange,
			onSearchInputChange: handleSearchInputChange,
			onSelectAssociate: handleSelectAssociate,
		}),
		[filter, handleRatingChange, handleSearchInputChange, handleSelectAssociate]
	);

	return <SocialMediaReviewDetailedDialogContext.Provider value={value}>{children}</SocialMediaReviewDetailedDialogContext.Provider>;
}

function useSocialMediaReviewDetailDialog() {
	const context = useContext(SocialMediaReviewDetailedDialogContext);
	if (context === undefined) {
		throw new Error("useSocialMediaReviewDetailDialog must be used within a SocialMediaReviewDetailedDialogProvider");
	}
	return context;
}

export { SocialMediaReviewDetailedDialogProvider, useSocialMediaReviewDetailDialog };
